import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { Box, Button, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { User02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import { Avatar, Drawer, toast } from "@outpave/ds";
import CardStatusType from "enums/CardStatusType";
import { getUserFacingErrorMessage } from "utils/graphql";
import { AssignedCardUser, EnrolledCard } from "./types";
import CardNetworkMark, { readCardNetwork } from "./CardNetworkMark";
import CardStatusBadge from "./CardStatusBadge";
import ComplianceNotice from "./ComplianceNotice";
import AssignedUserSelect, { formatUserOption } from "./AssignedUserSelect";

export interface CardDetailDrawerProps {
  open: boolean;
  /** Null while nothing is selected, so the drawer can animate out with content. */
  card: EnrolledCard | null;
  onClose: () => void;
  onAfterClose?: () => void;
  /** Absent while the server cannot assign users, which disables the picker. See README.md. */
  onAssignUser?: (card: EnrolledCard, user: AssignedCardUser) => Promise<void>;
  /** Absent while the server cannot re-enroll, which disables the button. See README.md. */
  onReenroll?: (cardId: string) => Promise<void>;
}

/** One field, label above value. See README.md for why not `DataRow`. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack gap="var(--space-1)">
    <Typography variant="body2" sx={{ color: "var(--color-text-tertiary)" }}>
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Stack>
);

/** Detail for one imported card, and where its user is assigned. See README.md. */
const CardDetailDrawer: React.FC<CardDetailDrawerProps> = ({
  open,
  card,
  onClose,
  onAfterClose,
  onAssignUser,
  onReenroll,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  if (!card) return null;

  const network = readCardNetwork(card.connectionNetwork);
  const maskedNumber = `•••• •••• •••• ${card.cardNumber}`;
  const unenrolled = card.enrollment === "unenrolled";

  const save = async (action: () => Promise<void>, successMessage: string) => {
    setSaving(true);
    try {
      await action();
      toast.success(successMessage);
      onClose();
    } catch (error) {
      toast.error(getUserFacingErrorMessage(error, t("Please try again!")));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = (user: AssignedCardUser) => {
    if (!onAssignUser) return;
    save(
      () => onAssignUser(card, user),
      t("CardAssignedToUser", { name: user.name }),
    );
  };

  const handleReenroll = () => {
    if (!onReenroll) return;
    save(() => onReenroll(card.id), t("Card re-enrolled"));
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onAfterClose={onAfterClose}
      title={t("Card")}
      loading={saving}
    >
      {/* Full height so the compliance notice can sit at the bottom. */}
      <Stack gap="var(--space-5)" sx={{ minHeight: "100%" }}>
        <Inline gap="var(--space-3)" align="center" justify="between">
          <Inline gap="var(--space-3)" align="center">
            <Avatar
              size="md"
              color="danger"
              sx={{ border: "none" }}
              icon={
                <HugeiconsIcon
                  icon={User02Icon}
                  size={16}
                  color="var(--color-text-danger)"
                />
              }
            />
            <Typography variant="h6">{card.cardholderName}</Typography>
          </Inline>
          <CardStatusBadge enrollment={card.enrollment} status={card.status} />
        </Inline>
        <Box>
          <Box
            sx={{
              aspectRatio: "1.586",
              maxWidth: "320px",
              display: "flex",
              margin: "0 auto",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "var(--space-5)",
              borderRadius: "var(--radius-lg)",
              border: "var(--border-width-1) solid var(--color-border)",
              backgroundImage:
                "linear-gradient(135deg, var(--color-bg-info), var(--color-bg-surface))",
            }}
          >
            <Box>
              <CardNetworkMark name={card.connectionName} size="md" />
            </Box>

            <Stack gap="var(--space-1)">
              <Typography variant="body1">{card.cardholderName}</Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "var(--type-family-label)",
                  letterSpacing: "0.08em",
                }}
              >
                {maskedNumber}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {`${network ?? card.connectionName} · ${t("External")}`}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box>
          {unenrolled ? (
            <Button
              variant="primary-outlined"
              disabled={!onReenroll || saving}
              onClick={handleReenroll}
            >
              {t("Re-enroll card")}
            </Button>
          ) : (
            // TODO: disabled until an `unenrollExternalCard` mutation exists. See README.md.
            <Button
              variant="danger-outlined"
              disabled
              sx={{ color: "var(--color-text-danger)" }}
            >
              {t("Unenroll card")}
            </Button>
          )}
        </Box>

        {/* The server refuses reassignment, so an assigned card shows its user. */}
        {card.assignedUser ? (
          <Field
            label={t("Assigned user")}
            value={formatUserOption(card.assignedUser).label}
          />
        ) : (
          <AssignedUserSelect
            key={card.id}
            value={null}
            onChange={handleAssignUser}
            readOnly={!onAssignUser || unenrolled}
            disabled={saving}
          />
        )}

        <Columns count={2} gap="var(--space-5)">
          <Field label={t("Cardholder")} value={card.cardholderName} />
          <Field label={t("Network")} value={network ?? "-"} />

          <Field label={t("Card account")} value={card.connectionName} />
          <div />

          <Field label={t("Card number")} value={`•••• ${card.cardNumber}`} />
          <Field
            label={t("Enrolled")}
            value={
              card.activatedAt
                ? dayjs(card.activatedAt).format("MM/DD/YYYY")
                : "-"
            }
          />

          <Field
            label={t("Sync status")}
            value={
              card.status === CardStatusType.ACTIVE
                ? t("SyncStatusActive")
                : t("SyncStatusPaused")
            }
          />
        </Columns>

        {/* Pinned to the bottom of the body rather than the drawer footer, so
            it scrolls away with the content on a short viewport. */}
        <Box sx={{ marginTop: "auto" }}>
          <ComplianceNotice subject="card" />
        </Box>
      </Stack>
    </Drawer>
  );
};

export default CardDetailDrawer;
